<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import TrustedChoice from '$lib/PageSections/TrustedChoice/TrustedChoice.svelte';
</script>

<Section>
	<Bubble backgroundColor="blue">
		<h2 class="text-left">To Our Customers</h2>
		<p class="text-left text-indow-white">
			At Indow, we’re committed to providing a premium solution for comfort and
			noise reduction. Recently, the costs of producing our high grade acrylic
			inserts and sourcing raw materials have risen significantly.
		</p>
		<p class="text-left text-indow-white">
			We have taken every internal measure possible to reduce our cost structure
			and absorb these increases to avoid passing them on to you. However,
			current economic conditions have finally forced our hand.
		</p>
		<p class="text-left font-bold text-indow-white">
			Effective July 1, 2026, our prices will increase by 10%.
		</p>
	</Bubble>
</Section>
<Section>
	<h2 class="text-left">Don’t Delay, Home Comfort Starts Now</h2>
	<p class="text-left">
		<span class="font-bold">Why start your project today?</span> Most homeowners wait
		until the first cold snap to think about insulation, but by then, the price increase
		will be in full effect. Acting now ensures you beat the hike while ensuring your
		home’s comfort at the best price:
	</p>
	<ul>
		<li>
			<p class="text-left">
				<a href="/solutions/window-noise-reduction">Block Summer Noise</a>: From
				leaf blowers and lawnmowers to loud neighbors and traffic, our inserts
				block up to 70% of outside noise so you can enjoy your home in peace.
			</p>
		</li>
		<li>
			<p class="text-left">
				<a href="/solutions/lower-energy-bills">Slash Cooling Costs</a>: Indow
				inserts don't just keep heat in; they keep the summer swelter out. Add a
				layer of high-performance insulation to your windows and give your AC a
				break.
			</p>
		</li>
		<li>
			<p class="text-left">
				Privacy & Light Control: Whether you need to block 100% of the early
				morning sun with our <a href="/solutions/blackout-window-solutions"
					>Sleep (Blackout)</a
				>
				inserts or obscure the view from the street with our
				<a href="/solutions/privacy-window-coverings">Privacy inserts</a>, you
				can secure a more secluded, restful home without the need for heavy,
				expensive window treatments.
			</p>
		</li>
	</ul>
	<Bubble backgroundColor="grey-dark">
		<h3 class="text-left">Secure Your Spot in our Production Line</h3>
		<p class="text-left">
			By finalizing your project now, you aren't just locking in a 10% lower
			rate, you’re securing your place in our current production cycle. Every
			Indow insert is handcrafted to your window’s specific measurements, and
			our capacity fills up quickly as the deadline approaches.
		</p>
		<p class="text-left">
			Acting today ensures your order enters our production line immediately,
			allowing you to beat the surge in volume and enjoy the benefits of a
			quieter, more energy efficient home without unnecessary delays.
		</p>
	</Bubble>
</Section>
<Section>
	<h2 class="text-left">Now is the Best Time to Order</h2>
	<p class="text-left">
		We pride ourselves on handcrafting every insert in the USA using premium
		materials. To maintain this standard of quality and service, our prices will
		increase across all product lines on July 1.
	</p>
	<div
		class="
    mb-2 w-full overflow-x-auto border-indow-grey bg-white
    max-md:col-span-full!
    md:rounded-lg md:border
  "
	>
		<table class="w-full border-collapse text-left">
			<thead class="bg-indow-blue text-white">
				<tr>
					<th class="px-2 py-4 text-base font-bold">Project Scope</th>
					<th class="px-2 py-4 text-right text-base font-bold">Current Price</th
					>
					<th class="px-2 py-4 text-right text-base font-bold"
						>Price Starting July 1</th
					>
					<th class="px-2 py-4 text-right text-base font-bold">Total Savings</th
					>
				</tr>
			</thead>
			<tbody class="divide-y divide-indow-grey text-indow-grey-dark">
				<tr class="bg-white">
					<td class="px-2 py-4 font-bold">Small Project (1-2 Windows)</td>
					<td class="px-2 py-4 text-right tabular-nums">$750</td>
					<td class="px-2 py-4 text-right tabular-nums">$825</td>
					<td
						class="px-2 py-4 text-right font-bold text-indow-blue tabular-nums"
						>Save $75</td
					>
				</tr>
				<tr class="bg-indow-grey-light">
					<td class="px-2 py-4 font-bold">Average Project (5-6 Windows)</td>
					<td class="px-2 py-4 text-right tabular-nums">$2,800</td>
					<td class="px-2 py-4 text-right tabular-nums">$3,080</td>
					<td
						class="px-2 py-4 text-right font-bold text-indow-blue tabular-nums"
						>Save $280</td
					>
				</tr>
				<tr class="bg-white">
					<td class="px-2 py-4 font-bold">Full Home (12+ Windows)</td>
					<td class="px-2 py-4 text-right tabular-nums">$6,500</td>
					<td class="px-2 py-4 text-right tabular-nums">$7,150</td>
					<td
						class="px-2 py-4 text-right font-bold text-indow-blue tabular-nums"
						>Save $650</td
					>
				</tr>
			</tbody>
		</table>
	</div>
	<p class="text-center">Find out what you’ll save by ordering today.</p>
	<Button href="/free-estimate">Get an instant estimate</Button>
</Section>

<TrustedChoice />
<Section>
	<h2 class="text-left">Secure Your Savings Today</h2>
	<p class="text-left">
		The window to lock in current pricing is closing. Because every Indow insert
		is custom measured and handcrafted, you must complete these steps before the
		July 1 deadline.
	</p>
	<ol class="list-disc">
		<li>
			<p class="text-left font-bold">Step 1: Request Your Estimate</p>
			<p class="text-left">
				The first step to locking in your price is getting a quote. <a
					href="/free-estimate">Get an estimate</a
				> now to get into our system and connect with an Indow expert.
			</p>
		</li>
		<li>
			<p class="text-left font-bold">Step 2: Complete Your Measurements</p>
			<p class="text-left">
				Once you have your quote, you'll need to submit your window
				measurements. Whether you use our simple DIY kit or our professional
				measuring service, starting this week ensures you have plenty of time to
				finalize your details.
			</p>
		</li>
		<li>
			<p class="text-left font-bold">Step 3: Finalize & Pay</p>
			<p class="text-left">
				To secure the 10% savings, your order must be finalized and paid for by
				midnight on June 30. Don't risk last minute delays, the sooner you pay,
				the sooner your inserts enter production. <a href="/free-estimate"
					>Get your estimate today</a
				>.
			</p>
		</li>
	</ol>
</Section>
